/**
 * Opener offers on the web coach page (PC-I71): what a trigger's line offers
 * the user to answer, for every trigger after the verdict's lighter session.
 *
 * The server speaks a line that carries an offer only to a client that lists
 * the offer's kind on its greeting request (`answers=`), so a kind goes into
 * `OPENER_OFFER_KINDS` together with its renderer, never before: a kind
 * declared with nothing to render it would show the line with no way to
 * answer it, and a dismissal there would spend the only ask.
 *
 * The answers each kind takes are the server's table
 * (`api/coach/offers.py`); `withdrawn` is the record line's Undo for every
 * kind while its answer can still change.
 *
 * The record line is drawn from what the server recorded (PC-I118), the
 * response to the answer, never from the answer the client sent: an add
 * kind's yes may be recorded as `already_there`, and "Added to {template}."
 * must not follow a yes that wrote nothing.
 */

import type { OfferAnswerOut, OpenerOffer } from "../../api/types"

/** The kinds this client renders, sent as `answers=` on every greeting request. */
export const OPENER_OFFER_KINDS: readonly string[] = [
  // Every kind below is drawn by the one `OfferControls`, from `offerView`
  // (#318).
  // #277: the injured area's week-later ask (PC-I68).
  "injury_check",
  // #273: a region gone quiet, an exercise added to a template (PC-I75).
  "region_add",
  // #274: a stale routine's one offer (PC-I75). The web declares it too
  // (PC-D21), with an Android parity waiver.
  "stale_add",
  // #272's plateau: a rep range and a deload, from `adjustOfferPresentation`,
  // and the variation, from the shared add offer's `addOfferPresentation`.
  "rep_range",
  "variation",
  "deload",
]

/** The record line's Undo, taken by every kind while `can_change` holds. */
export const OFFER_WITHDRAWN = "withdrawn"

/**
 * The answers that fold the line away, per kind: the server's
 * `OfferKind.folds` (`api/coach/offers.py`), which dismisses the row as it
 * records the answer. After one the opener loads again, as it does for a
 * dismissal (PC-I75: a decline records itself and folds the line away, with
 * no record line and no Undo). A kind absent here folds nothing.
 */
export const OFFER_FOLDS: Readonly<Record<string, readonly string[]>> = {
  stale_add: ["declined"],
  region_add: ["declined"],
  variation: ["declined"],
  rep_range: ["declined"],
  deload: ["declined"],
}

/** Whether the recorded answer to an offer of this kind folds its line away. */
export function foldsAway(kind: string | undefined, recorded: string | null | undefined): boolean {
  if (kind === undefined || recorded == null || !Object.hasOwn(OFFER_FOLDS, kind)) return false
  return OFFER_FOLDS[kind].includes(recorded)
}

/** The greeting request, listing the kinds this client renders. */
export function greetingPath(
  { firstRun = false, kinds = OPENER_OFFER_KINDS }: { firstRun?: boolean; kinds?: readonly string[] } = {},
): string {
  const params = new URLSearchParams()
  if (firstRun) params.set("first_run", "1")
  if (kinds.length > 0) params.set("answers", kinds.join(","))
  const query = params.toString()
  return query ? `/api/coach/home-greeting?${query}` : "/api/coach/home-greeting"
}

/**
 * What the server recorded for an offer's answer (PC-I118), read from its
 * response: the answer it stored, which can differ from the one sent, and
 * whether that answer may still change (no Undo where it may not). `null`
 * when the response is not the shape the server promises, so the caller loads
 * the opener again and shows the server's own record instead of guessing at
 * it. A response that does not say the answer can change is read as "no", so
 * no Undo is offered that the server would refuse.
 */
export function readRecorded(body: unknown): Pick<OpenerOffer, "answer" | "can_change"> | null {
  if (typeof body !== "object" || body === null) return null
  const { answer, can_change } = body as Partial<Record<keyof OfferAnswerOut, unknown>>
  if (answer != null && typeof answer !== "string") return null
  return { answer: answer ?? null, can_change: can_change === true }
}
